import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LogIn, UserPlus, Shield, Zap, Users, Award, Mail, Phone, MapPin } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_emakqq-brand/artifacts/cy3dezo1_download.jpg" 
                alt="EmakQQ Logo" 
                className="h-16 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-700 hover:text-[#4CAF50] font-medium transition-colors duration-300">
                Home
              </a>
              <a href="#about" className="text-gray-700 hover:text-[#4CAF50] font-medium transition-colors duration-300">
                Tentang Kami
              </a>
              <a href="#contact" className="text-gray-700 hover:text-[#4CAF50] font-medium transition-colors duration-300">
                Kontak
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
                className="border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white transition-all duration-300"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Member Login
              </Button>
              <Button 
                onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
                className="bg-[#4CAF50] hover:bg-[#388E3C] text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Daftar
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-green-50 to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="px-4 py-2 bg-[#4CAF50]/10 text-[#388E3C] rounded-full text-sm font-semibold">
                  #1 Platform Betting Terpercaya Indonesia
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Pengalaman Betting Online
                <span className="text-[#4CAF50]"> Terbaik</span> di Indonesia
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                EmakQQ menghadirkan platform betting online yang aman, terpercaya, dan mudah diakses. 
                Nikmati berbagai permainan berkualitas dengan layanan 24/7 dan sistem pembayaran tercepat.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
                  className="bg-[#4CAF50] hover:bg-[#388E3C] text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  Daftar Sekarang
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
                  className="border-2 border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white text-lg px-8 py-6 transition-all duration-300"
                >
                  <LogIn className="mr-2 h-5 w-5" />
                  Member Login
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center space-x-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#4CAF50]">100K+</div>
                  <div className="text-sm text-gray-600">Member Aktif</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#4CAF50]">24/7</div>
                  <div className="text-sm text-gray-600">Customer Support</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#4CAF50]">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime</div>
                </div>
              </div>
            </div>

            {/* Right Content - Logo/Image */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_emakqq-brand/artifacts/cy3dezo1_download.jpg" 
                  alt="EmakQQ" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-[#4CAF50]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-green-200/30 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Mengapa Memilih <span className="text-[#4CAF50]">EmakQQ</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kami berkomitmen memberikan pengalaman betting terbaik dengan keamanan dan kenyamanan maksimal
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Aman & Terpercaya',
                description: 'Sistem keamanan berlapis dengan enkripsi data tingkat bank untuk melindungi informasi Anda'
              },
              {
                icon: Zap,
                title: 'Transaksi Cepat',
                description: 'Proses deposit dan withdraw tercepat, dana masuk dalam hitungan menit'
              },
              {
                icon: Users,
                title: 'Support 24/7',
                description: 'Tim customer service profesional siap membantu Anda kapan saja'
              },
              {
                icon: Award,
                title: 'Bonus Melimpah',
                description: 'Dapatkan berbagai bonus menarik dan promo eksklusif setiap hari'
              }
            ].map((feature, index) => (
              <Card key={index} className="border-2 hover:border-[#4CAF50] transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4CAF50]/10 rounded-2xl mb-4">
                    <feature.icon className="h-8 w-8 text-[#4CAF50]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_emakqq-brand/artifacts/cy3dezo1_download.jpg" 
                alt="Tentang EmakQQ" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
            
            <div className="order-1 lg:order-2 space-y-6">
              <div>
                <span className="text-[#4CAF50] font-semibold text-lg">Tentang Kami</span>
                <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">
                  Platform Betting Online Terpercaya di Indonesia
                </h2>
              </div>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>EmakQQ</strong> adalah platform betting online terkemuka di Indonesia yang telah melayani 
                lebih dari 100,000 member aktif sejak didirikan. Kami menghadirkan pengalaman bermain yang aman, 
                fair, dan menguntungkan dengan berbagai pilihan permainan berkualitas tinggi.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Dengan teknologi terdepan dan tim profesional yang berpengalaman, kami memastikan setiap transaksi 
                Anda berjalan lancar dan aman. Sistem keamanan berlapis kami melindungi data pribadi dan finansial 
                Anda dengan standar enkripsi tingkat perbankan internasional.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-[#4CAF50]">100K+</div>
                  <div className="text-gray-600">Member Terdaftar</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-[#4CAF50]">5+</div>
                  <div className="text-gray-600">Tahun Pengalaman</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-[#4CAF50]">50+</div>
                  <div className="text-gray-600">Permainan Tersedia</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-[#4CAF50]">24/7</div>
                  <div className="text-gray-600">Layanan Pelanggan</div>
                </div>
              </div>

              <Button 
                size="lg" 
                onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
                className="bg-[#4CAF50] hover:bg-[#388E3C] text-white px-8 py-6 mt-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Bergabung Sekarang
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Hubungi <span className="text-[#4CAF50]">Kami</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tim customer service kami siap membantu Anda 24/7. Jangan ragu untuk menghubungi kami!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Mail,
                title: 'Email',
                info: 'support@emakqq.com',
                link: 'mailto:support@emakqq.com'
              },
              {
                icon: Phone,
                title: 'WhatsApp',
                info: '+62 812-3456-7890',
                link: 'https://wa.me/6281234567890'
              },
              {
                icon: MapPin,
                title: 'Live Chat',
                info: 'Tersedia 24/7',
                link: '#'
              }
            ].map((contact, index) => (
              <Card key={index} className="border-2 hover:border-[#4CAF50] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4CAF50]/10 rounded-2xl mb-4">
                    <contact.icon className="h-8 w-8 text-[#4CAF50]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{contact.title}</h3>
                  <a 
                    href={contact.link} 
                    className="text-lg text-[#4CAF50] hover:text-[#388E3C] font-medium transition-colors duration-300"
                  >
                    {contact.info}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#4CAF50] to-[#388E3C]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Siap Memulai Pengalaman Betting Terbaik?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Bergabunglah dengan 100,000+ member yang sudah merasakan keuntungan bermain di EmakQQ
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
              className="bg-white text-[#4CAF50] hover:bg-gray-100 text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Daftar Gratis
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open('https://newlink.fyi/emakqq/', '_blank')}
              className="border-2 border-white text-white hover:bg-white hover:text-[#4CAF50] text-lg px-8 py-6 transition-all duration-300"
            >
              <LogIn className="mr-2 h-5 w-5" />
              Login Member
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_emakqq-brand/artifacts/cy3dezo1_download.jpg" 
                alt="EmakQQ" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 leading-relaxed">
                EmakQQ adalah platform betting online terpercaya di Indonesia dengan sistem keamanan terbaik 
                dan layanan customer support 24/7.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4">Menu</h3>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-[#4CAF50] transition-colors duration-300">Home</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-[#4CAF50] transition-colors duration-300">Tentang Kami</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-[#4CAF50] transition-colors duration-300">Kontak</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-bold mb-4">Kontak</h3>
              <ul className="space-y-2">
                <li className="text-gray-400">Email: support@emakqq.com</li>
                <li className="text-gray-400">WA: +62 812-3456-7890</li>
                <li className="text-gray-400">Support: 24/7 Online</li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 EmakQQ - Platform Betting Online Terpercaya Indonesia. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;